import { forwardRef } from "react";
import * as t from "@rekajs/types";
import type { ComponentType } from "@repo/editor/types";
import type {
  ComponentSettings,
  ComponentSettingsProps,
} from "@repo/editor/externalComponents";
import { ArrowButton } from "../components/common/arrow-button";
import VideoPlayer from "../components/common/video-player";
import Link from "next/link";
import { useApp } from "@repo/app/useApp";

export const OverflowSectionDefinition: ComponentSettingsProps = {
  label: {
    label: "بخش با انتقال",
    section: "متن",
    type: "html",
    default: "",
  },
  image: {
    label: "تصویر",
    section: "تصویر",
    type: "image",
    default: "",
  },
  link: {
    label: "لینک",
    section: "لینک",
    type: "string",
    default: "",
  },
  textColor: {
    label: "رنگ متن",
    section: "متن",
    type: "colorPicker",
    default: "#6A6A6A",
  },
  backgroundColor: {
    label: "رنگ پس زمینه",
    section: "پس زمینه",
    type: "colorPicker",
    default: "#DCDCDC",
  },
};

export interface OverflowSectionProps {
  label: string;
  image: string;
  textColor?: string;
  link: string;
  backgroundColor?: string;
}

export const OverflowSection = forwardRef<HTMLDivElement, OverflowSectionProps>(
  (props, ref) => {
    const app = useApp();
    const isEditor = app.rendererMode === "editor";
    return (
      <div className="w-full h-full pb-[60px] md:pb-0 group" ref={ref}>
        <div className="flex items-center justify-center relative w-full h-full">
          <div className="w-2/5" />
          <div
            className="w-3/5 h-full max-h-[650px]"
            style={{ backgroundColor: props.backgroundColor }}
          >
            {props.image.includes("mp4") ? (
              <div className="size-full">
                <VideoPlayer link={props.image || ""} />
              </div>
            ) : (
              <img
                alt={props.label}
                className="w-full pr-10 pl-5 h-full max-h-[500px] object-contain"
                onError={(e) => {
                  e.currentTarget.src = "/images/placeholder.png";
                }}
                src={props.image}
              />
            )}
          </div>
          <div className="absolute inset-0 md:bottom-10 -bottom-10 left-0 flex flex-col-reverse md:flex-col items-start md:justify-end md:content-end gap-[20px] md:gap-[40px] container mx-auto">
            <div
              className="text-2xl w-full text-end md:text-start md:text-4xl font-medium md:leading-[57.88px] tracking-[0.12px] md:tracking-[0.01em] py-0 my-0 ql-editor"
              dangerouslySetInnerHTML={{ __html: props.label }}
              style={{
                color:
                  props.textColor ||
                  String(OverflowSectionDefinition.textColor.default),
              }}
            />
            {!isEditor && props.link && props.link !== "#" ? (
              <Link className="rtl:mr-20 ltr:ml-20" href={props.link}>
                <ArrowButton />
              </Link>
            ) : (
              <div className="rtl:mr-20 ltr:ml-20">
                <ArrowButton />
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }
) as ComponentType<OverflowSectionProps>;
OverflowSection.displayName = "OverflowSection";

export const OverflowSectionSettings: ComponentSettings = {
  name: "EOverflowSection",
  props: OverflowSectionDefinition,
  label: "بخش سرریز",
  widgets: {
    label: "بخش سرریز",
    template: () =>
      t.componentTemplate({
        component: t.identifier({ name: "EOverflowSection", external: true }),
        props: {
          label: t.literal({ value: "بخش سرریز" }),
        },
      }),
  },
};

OverflowSection.componentSettings = OverflowSectionSettings;
